import type { ChecklistItem as FlatChecklistItem } from '../types/schema'
import type { TimelineElement } from '../types/timelineElement'
import type { HeroGradientId } from '../types/workspace'
import type { ReportRecord } from '../types/report'
// Circular-safe: ariaAndCoSeed only imports types from this file, not values.

export type ExploreWorkspaceSeedMetric = {
  label: string
  value: string
}

export type ExploreWorkspaceSeed = {
  id: string
  name: string
  slug: string
  summary: string
  tags: string[]
  metrics: ExploreWorkspaceSeedMetric[]
  currency: string
  budgetLabel: string
  quotaLabel: string
  sensitiveDemo: {
    label: string
    items: string[]
  }
  members: Array<{
    name: string
    email: string
    role: string
  }>
  elements: TimelineElement[]
  checklistItems: FlatChecklistItem[]
  /** Report Center records shown in a demo workspace. */
  reports?: ReportRecord[]
  /** Optional cover art + display metadata used by the Explore hub. */
  featured?: boolean
  heroGradient?: HeroGradientId
  coverImagePath?: string
  verified?: boolean
}

const board = 'DASHBOARD' as const
const demoUserIds = ['demo-admin', 'demo-editor', 'demo-viewer', 'demo-helper'] as const

const metric = (label: string, value: string): ExploreWorkspaceSeedMetric => ({ label, value })

const element = (
  workspaceId: string,
  slug: string,
  input: {
    id: string
    type: TimelineElement['type']
    title: string
    description: string
    parentId: string | null
    routePath: string
    parentRoutePath?: string
    progress: number
    status: TimelineElement['boardStatus']
    priority: number
    startDate: string
    endDate: string
    budgetLimit: number
    estimatedCost: number
    color: `#${string}`
  },
): TimelineElement => ({
  id: input.id,
  type: input.type,
  title: input.title,
  description: input.description,
  weight: input.type === 'GOAL' ? 10 : input.type === 'TASK' ? 5 : 2,
  progress: input.progress,
  parentId: input.parentId,
  board,
  publicId: `${slug.toUpperCase()}-${input.routePath.toUpperCase()}`,
  routePath: input.routePath,
  parentRoutePath: input.parentRoutePath ?? '',
  workspaceId,
  startDate: input.startDate,
  endDate: input.endDate,
  boardStatus: input.status,
  workflowStatus: input.status,
  priority: input.priority,
  dependencyNodeIds: [],
  recordVersion: 1,
  statusSyncOrigin: 'SEED',
  statusSyncVersion: 1,
  readinessScore: input.progress,
  readyChecklistCount: Math.round(input.progress / 25),
  totalChecklistCount: 4,
  budgetLimit: input.budgetLimit,
  estimatedCost: input.estimatedCost,
  color: input.color,
  checklist: [],
  readAccess: [...demoUserIds],
  writeAccess: ['demo-admin', 'demo-editor'],
  directReadAccess: [...demoUserIds],
  directWriteAccess: ['demo-admin', 'demo-editor'],
  lastUpdatedBy: 'demo-admin',
})

const checklist = (
  workspaceId: string,
  slug: string,
  input: {
    id: string
    parentContainerId: string
    title: string
    description: string
    routePath: string
    parentRoutePath: string
    completed: boolean
    status: FlatChecklistItem['workflowStatus']
    priority: number
    startDate: string
    endDate: string
    x: number
    y: number
    budgetLimit: number
    actualSpend: number
    estimatedCost: number
    soldUnits: number
    assigneeName: string
  },
): FlatChecklistItem => ({
  id: input.id,
  parentContainerId: input.parentContainerId,
  workspaceId,
  workspaceSlug: slug,
  board,
  publicId: `${slug.toUpperCase()}-${input.routePath.toUpperCase()}`,
  routePath: input.routePath,
  parentRoutePath: input.parentRoutePath,
  title: input.title,
  description: input.description,
  completionCriteria: 'Demo criteria: owner evidence, status update, and dependency review are visible in preview mode.',
  boardStatus: input.completed ? 'COMPLETE' : input.status === 'ONGOING' ? 'IN_PROGRESS' : 'TODO',
  workflowStatus: input.status,
  priority: input.priority,
  startDate: input.startDate,
  endDate: input.endDate,
  recordVersion: 1,
  statusSyncOrigin: 'SEED',
  statusSyncVersion: 1,
  createdById: 'demo-admin',
  createdByName: 'Demo Admin',
  assigneeId: input.assigneeName.toLowerCase().replace(/\s+/g, '-'),
  assigneeName: input.assigneeName,
  lastUpdatedBy: 'demo-admin',
  lastUpdatedByName: 'Demo Admin',
  isCompleted: input.completed,
  weightage: input.completed ? 2 : 3,
  position: { x: input.x, y: input.y },
  workspaceCanvasLayoutVersion: 1,
  prerequisites: [],
  dependencyNodeIds: [],
  blockedByExternal: null,
  requiresProofFile: !input.completed,
  requiresLocationPin: false,
  isHelperVisible: true,
  budgetLimit: input.budgetLimit,
  actualSpend: input.actualSpend,
  estimatedCost: input.estimatedCost,
  soldUnits: input.soldUnits,
  currencyCode: 'USD',
  complexityStars: input.priority,
  urgencyStars: input.priority,
  importanceStars: Math.min(5, input.priority + 1),
  estimatedHours: 8 + input.priority * 2,
  actualCost: input.actualSpend,
  scheduleMode: 'DATED',
  scheduleStartDate: input.startDate,
  scheduleEndDate: input.endDate,
  hasTimeWindow: true,
  scheduleTimeFrom: '09:00',
  scheduleTimeTo: '17:30',
})

const buildSeed = (
  id: string,
  name: string,
  slug: string,
  summary: string,
  tags: string[],
  budgetLabel: string,
  quotaLabel: string,
  goalTitles: readonly [string, string],
  color: `#${string}`,
): ExploreWorkspaceSeed => {
  const firstGoalId = `${id}-goal-1`
  const secondGoalId = `${id}-goal-2`
  const firstTaskId = `${id}-task-1`
  const secondTaskId = `${id}-task-2`
  const elements = [
    element(id, slug, {
      id: firstGoalId,
      type: 'GOAL',
      title: goalTitles[0],
      description: `${name} demo goal with mixed completed and pending work, budget limits, quota targets, and assignee visibility.`,
      parentId: null,
      routePath: 'g1',
      progress: 62,
      status: 'IN_PROGRESS',
      priority: 4,
      startDate: '2026-07-01',
      endDate: '2026-08-14',
      budgetLimit: 240000,
      estimatedCost: 198000,
      color,
    }),
    element(id, slug, {
      id: firstTaskId,
      type: 'TASK',
      title: 'Planning and readiness',
      description: 'Owners align scope, dependencies, assets, proof requirements, and reporting gates.',
      parentId: firstGoalId,
      routePath: 'g1/t1',
      parentRoutePath: 'g1',
      progress: 78,
      status: 'IN_PROGRESS',
      priority: 4,
      startDate: '2026-07-02',
      endDate: '2026-07-24',
      budgetLimit: 90000,
      estimatedCost: 74000,
      color,
    }),
    element(id, slug, {
      id: secondGoalId,
      type: 'GOAL',
      title: goalTitles[1],
      description: `${name} execution goal showing future work, blocked items, and downstream reporting examples.`,
      parentId: null,
      routePath: 'g2',
      progress: 31,
      status: 'TODO',
      priority: 3,
      startDate: '2026-08-15',
      endDate: '2026-09-30',
      budgetLimit: 180000,
      estimatedCost: 152000,
      color,
    }),
    element(id, slug, {
      id: secondTaskId,
      type: 'TASK',
      title: 'Launch execution',
      description: 'Track final handoffs, completion gates, spend, and quota movement.',
      parentId: secondGoalId,
      routePath: 'g2/t1',
      parentRoutePath: 'g2',
      progress: 22,
      status: 'TODO',
      priority: 3,
      startDate: '2026-08-16',
      endDate: '2026-09-12',
      budgetLimit: 70000,
      estimatedCost: 61000,
      color,
    }),
  ]

  const checklistItems = [
    checklist(id, slug, {
      id: `${id}-check-1`,
      parentContainerId: firstTaskId,
      title: 'Confirm owner map and roles',
      description: 'Demo includes Admin, Editor, Viewer, Readonly, and Helper members with fake identities.',
      routePath: 'g1/t1/c1',
      parentRoutePath: 'g1/t1',
      completed: true,
      status: 'COMPLETED',
      priority: 3,
      startDate: '2026-07-02',
      endDate: '2026-07-05',
      x: 80,
      y: 80,
      budgetLimit: 10000,
      actualSpend: 8200,
      estimatedCost: 9000,
      soldUnits: 18,
      assigneeName: 'Mira Example',
    }),
    checklist(id, slug, {
      id: `${id}-check-2`,
      parentContainerId: firstTaskId,
      title: 'Review budget and quota dashboard',
      description: 'Budget, forecast, spend, quota progress, and risk status are intentionally populated.',
      routePath: 'g1/t1/c2',
      parentRoutePath: 'g1/t1',
      completed: false,
      status: 'ONGOING',
      priority: 4,
      startDate: '2026-07-08',
      endDate: '2026-07-18',
      x: 430,
      y: 90,
      budgetLimit: 18000,
      actualSpend: 7200,
      estimatedCost: 15100,
      soldUnits: 9,
      assigneeName: 'Noah Example',
    }),
    checklist(id, slug, {
      id: `${id}-check-3`,
      parentContainerId: secondTaskId,
      title: 'Attach proof and sensitive demo notes',
      description: 'Uses fake vendor, invoice, and stakeholder details so readonly sensitive-view behavior is visible.',
      routePath: 'g2/t1/c1',
      parentRoutePath: 'g2/t1',
      completed: false,
      status: 'ASSIGNED',
      priority: 5,
      startDate: '2026-08-18',
      endDate: '2026-08-28',
      x: 120,
      y: 310,
      budgetLimit: 22000,
      actualSpend: 0,
      estimatedCost: 20600,
      soldUnits: 0,
      assigneeName: 'Asha Example',
    }),
    checklist(id, slug, {
      id: `${id}-check-4`,
      parentContainerId: secondTaskId,
      title: 'Close reporting and handoff',
      description: 'Final report, activity trail, and owner handoff remain viewable but disabled for edits.',
      routePath: 'g2/t1/c2',
      parentRoutePath: 'g2/t1',
      completed: false,
      status: 'CREATED',
      priority: 2,
      startDate: '2026-09-01',
      endDate: '2026-09-12',
      x: 470,
      y: 320,
      budgetLimit: 12000,
      actualSpend: 0,
      estimatedCost: 9600,
      soldUnits: 0,
      assigneeName: 'Sam Example',
    }),
  ]

  return {
    id,
    name,
    slug,
    summary,
    tags,
    metrics: [
      metric('Budget', budgetLabel),
      metric('Quota', quotaLabel),
    ],
    currency: 'USD',
    budgetLabel,
    quotaLabel,
    sensitiveDemo: {
      label: 'Fake sensitive demo data',
      items: [
        'demo-finance@example.test',
        'Invoice DEMO-2048',
        'Vendor bank token: DEMO-ONLY-9931',
      ],
    },
    members: [
      { name: 'Mira Example', email: 'mira@example.test', role: 'ADMIN' },
      { name: 'Noah Example', email: 'noah@example.test', role: 'EDITOR' },
      { name: 'Asha Example', email: 'asha@example.test', role: 'VIEWER' },
      { name: 'Sam Example', email: 'sam@example.test', role: 'HELPER' },
    ],
    elements,
    checklistItems,
  }
}

import { ARIA_AND_CO_SEED } from './ariaAndCoSeed'
import { INTEGRATIONS_DEMO_SEED } from './integrationsDemoSeed'
import { SCHOOL_DEMO_SEED } from './edu/schoolDemoSeed'
import { COACHING_DEMO_SEED } from './edu/coachingDemoSeed'
import { SPORTS_ACADEMY_DEMO_SEED } from './edu/sportsAcademyDemoSeed'

export const EXPLORE_WORKSPACE_SEEDS: ExploreWorkspaceSeed[] = [
  ARIA_AND_CO_SEED,
  INTEGRATIONS_DEMO_SEED,
  SCHOOL_DEMO_SEED,
  COACHING_DEMO_SEED,
  SPORTS_ACADEMY_DEMO_SEED,
  buildSeed(
    'explore-seed-product-launch',
    'Product Launch Demo',
    'demo-product-launch',
    'A cross-functional launch workspace with roadmap, QA, go-to-market, budget, quota, and proof gates.',
    ['launch', 'product', 'budget', 'quota'],
    '$4,200 / $5,900',
    '27 / 80 signups',
    ['Launch readiness', 'Post-launch optimization'],
    '#0ea5e9',
  ),
  buildSeed(
    'explore-seed-construction',
    'Construction Project Demo',
    'demo-construction-project',
    'Site planning, procurement, inspections, materials, crew handoffs, and completion evidence in one workspace.',
    ['construction', 'site', 'procurement', 'inspection'],
    '$62k / $88k',
    '14 / 22 milestones',
    ['Foundation and permits', 'Interior fit-out'],
    '#16a34a',
  ),
  buildSeed(
    'explore-seed-wedding',
    'Wedding Planning Demo',
    'demo-wedding-planning',
    'Event planning with guest ops, vendor budgets, venue milestones, schedule windows, and day-of checklists.',
    ['event', 'wedding', 'vendors', 'schedule'],
    '$18k / $24k',
    '126 / 180 guests',
    ['Vendor and venue readiness', 'Guest experience'],
    '#db2777',
  ),
  buildSeed(
    'explore-seed-study-plan',
    'Study Plan Demo',
    'demo-study-plan',
    'A learning workspace with curriculum goals, revision cadence, mock tests, completion proof, and weekly quotas.',
    ['study', 'learning', 'habits', 'quota'],
    '$310 / $500',
    '42 / 75 hours',
    ['Core syllabus coverage', 'Exam rehearsal'],
    '#7c3aed',
  ),
  buildSeed(
    'explore-seed-startup-ops',
    'Startup Operations Demo',
    'demo-startup-operations',
    'Operating rhythms for hiring, customer ops, finance approvals, runway reviews, and founder reporting.',
    ['startup', 'operations', 'finance', 'team'],
    '$31k / $45k',
    '9 / 15 ops loops',
    ['Operating cadence', 'Scale-up readiness'],
    '#ea580c',
  ),
]
